import React, {
	useState, useMemo, useEffect, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import * as Comlink from 'comlink';
import { GreenroomState } from '../../stores/types/types';
import type { GraphicInterface } from '../../stores/types/graphics';
import type { MediaItem, MediaSearchResults } from '../../stores/types/media';
import { MEDIA_TABS } from '../../stores/reducers/session';
import { MEDIA_FILTER_TYPES, MEDIA_SORT_TYPES } from '../../types/constants';
import { setMediaFilters } from '../../stores/actions/user-interface';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import Popover from '../general-components/popover';
/* eslint-disable-next-line */
import SearchWorker from 'worker-loader!../../workers/media-search-worker';
import WaitingIndicator from '../general-components/waiting-indicator';

interface Props {
	uploading: boolean;
	filteredMedia: MediaItem[];
	filteredRecordings: MediaItem[];
	filteredGraphics: GraphicInterface[];
	handleIsMediaFiltered: (isMediaFiltered: boolean) => void;
	onFilter: (results: MediaSearchResults) => void;
	media: MediaItem[];
	recordings: MediaItem[];
	graphics: GraphicInterface[];
	subtab: string;
	isForModal: boolean;
}

let searchWorker: any;

(async function init() {
	const worker = new SearchWorker();
	searchWorker = Comlink.wrap(worker) as any;
}());

const loadingEl = <div style={{
	minHeight: '20px',
	maxHeight: '20px',
	margin: '0 auto',
}}><WaitingIndicator /></div>;

export default function MediaMenu(props: Props) {
	const {
		uploading,
		filteredMedia,
		filteredRecordings,
		filteredGraphics,
		handleIsMediaFiltered,
		onFilter,
		media,
		recordings,
		graphics,
		subtab,
		isForModal,
	} = props;

	const {
		selectedFilterType,
		selectedSortType,
	} = useSelector((state: GreenroomState) => state.UserInterfaceReducer.mediaFilters);

	const dispatch = useDispatch();

	const [searchString, setSearchString] = useState<string>('');
	const [filterDropdownOpen, setFilterDropdownOpen] = useState<boolean>(false);
	const [sortDropdownOpen, setSortDropdownOpen] = useState<boolean>(false);
	const [filterLoading, setFilterLoading] = useState<boolean>(false);

	const searchDebounce = useRef<NodeJS.Timeout | null>(null);

	const isMediaFiltered = useMemo(
		() => !(
			!searchString
			&& selectedFilterType === MEDIA_FILTER_TYPES.ALL
			&& selectedSortType === MEDIA_SORT_TYPES.NONE
		),
		[searchString, selectedFilterType, selectedSortType],
	);

	const mediaFilterOptions = useMemo(
		() => (subtab === MEDIA_TABS.FILES
			? Object.values(MEDIA_FILTER_TYPES)
			: Object.values(MEDIA_FILTER_TYPES).filter((type) => type !== MEDIA_FILTER_TYPES.DOCUMENTS)),
		[subtab],
	);

	const resultsString = useMemo(() => {
		switch (subtab) {
			case MEDIA_TABS.FILES: return `${filteredMedia.length} file${filteredMedia.length === 1 ? '' : 's'}  found`;
			case MEDIA_TABS.RECORDINGS: return `${filteredRecordings.length} recording${filteredRecordings.length === 1 ? '' : 's'} found`;
			case MEDIA_TABS.OVERLAYS: return `${filteredGraphics.length} overlay${filteredGraphics.length === 1 ? '' : 's'} found`;
			case MEDIA_TABS.AUDIO: return `${filteredMedia.length} audio found`;
			default: return '';
		}
	}, [subtab, filteredMedia, filteredRecordings, filteredGraphics]);

	useEffect(() => {
		searchWorker
			.sort(
				media,
				recordings,
				graphics,
				selectedFilterType,
				selectedSortType,
			)
			.then(async () => {
				const results = await searchWorker.results;
				onFilter(results);
				if (!searchString) setSearchString(await searchWorker.raw);
				setFilterLoading(false);
			});
	}, [media, recordings, graphics, selectedFilterType, selectedSortType]);

	useEffect(() => {
		if (subtab === MEDIA_TABS.OVERLAYS && selectedFilterType === MEDIA_FILTER_TYPES.DOCUMENTS) {
			dispatch(setMediaFilters({
				selectedFilterType: MEDIA_FILTER_TYPES.ALL,
				selectedSortType,
			}));
		}
	}, [subtab, selectedFilterType]);

	useEffect(() => {
		handleIsMediaFiltered(isMediaFiltered);
	}, [isMediaFiltered]);

	const filterMedia = (
		inputSearchString: string,
		inputFilterType: string,
		inputSortType: string,
	) => {
		searchWorker
			.search(
				inputSearchString,
				[...media],
				[...recordings],
				[...graphics],
				inputFilterType,
				inputSortType,
			)
			.then(async () => {
				const results = await searchWorker.results;
				onFilter(results);
				setFilterLoading(false);
			});
	};

	const handleFilterMedia = ({ target }: React.KeyboardEvent<HTMLInputElement>) => {
		if (!(target instanceof HTMLInputElement)) return;

		if (searchDebounce.current) clearTimeout(searchDebounce.current);
		searchDebounce.current = setTimeout(() => {
			filterMedia(target.value, selectedFilterType, selectedSortType);
		}, 250);
	};

	const handleClearFilters = () => {
		searchWorker.clear();
		setSearchString('');
		dispatch(setMediaFilters({
			selectedFilterType: MEDIA_FILTER_TYPES.ALL,
			selectedSortType: MEDIA_SORT_TYPES.NONE,
		}));
	};

	const handleClickSort = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		if (selectedSortType === MEDIA_SORT_TYPES.RECENT_ASC) {
			dispatch(setMediaFilters(
				{ selectedFilterType, selectedSortType: MEDIA_SORT_TYPES.RECENT_DESC },
			));
		} else if (selectedSortType === MEDIA_SORT_TYPES.RECENT_DESC) {
			dispatch(setMediaFilters(
				{ selectedFilterType, selectedSortType: MEDIA_SORT_TYPES.RECENT_ASC },
			));
		} else if (selectedSortType === MEDIA_SORT_TYPES.NAME_ASC) {
			dispatch(setMediaFilters(
				{ selectedFilterType, selectedSortType: MEDIA_SORT_TYPES.NAME_DESC },
			));
		} else if (selectedSortType === MEDIA_SORT_TYPES.NAME_DESC) {
			dispatch(setMediaFilters(
				{ selectedFilterType, selectedSortType: MEDIA_SORT_TYPES.NAME_ASC },
			));
		} else if (selectedSortType === MEDIA_SORT_TYPES.SHOT_NUMBER_ASC) {
			dispatch(setMediaFilters(
				{ selectedFilterType, selectedSortType: MEDIA_SORT_TYPES.SHOT_NUMBER_DESC },
			));
		} else if (selectedSortType === MEDIA_SORT_TYPES.SHOT_NUMBER_DESC) {
			dispatch(setMediaFilters(
				{ selectedFilterType, selectedSortType: MEDIA_SORT_TYPES.SHOT_NUMBER_ASC },
			));
		}
	};

	const openSelectFile = () => {
		if (!uploading) {
			document.getElementById('file-input')?.click();
		}
	};

	const handleSelectSort = (defaultType: string, alternateType: string) => {
		// eslint-disable-next-line no-unused-expressions
		selectedSortType === defaultType
			? dispatch(setMediaFilters({ selectedFilterType, selectedSortType: alternateType }))
			: dispatch(setMediaFilters({ selectedFilterType, selectedSortType: defaultType }));
		setSortDropdownOpen(false);
	};

	useEffect(() => {
		setSortDropdownOpen(false);
		setFilterDropdownOpen(false);
	}, [subtab]);

	const filterDropdown = useMemo(() => (
			<div className="filter-container" onClick={() => setFilterDropdownOpen(true)}>
				<div className={classNames('filter-label bl-font-p2', {
					isopen: filterDropdownOpen,
					isapplied: selectedFilterType !== MEDIA_FILTER_TYPES.ALL,
				})}>
					<span>
						{/* eslint-disable-next-line no-nested-ternary */}
						{selectedFilterType === MEDIA_FILTER_TYPES.ALL
							? subtab === MEDIA_TABS.FILES
								? `${selectedFilterType} files`
								: `${selectedFilterType} overlays`
							: selectedFilterType}
					</span>
					<Icon
						name={ICONS.KEYBOARD_ARROW_DOWN}
						color={COLORS.WHITE}
						size={10}
					/>
				</div>
				<Popover open={filterDropdownOpen} close={() => setFilterDropdownOpen(false)}>
					<div className='filter-options bl-font-p2'>
						<div className='filter-option-header'><span className='bl-font-c1'>SHOW</span></div>
						{mediaFilterOptions.map((filter: string, index: number) => (
							<div
								key={index}
								className={classNames('filter-option', filter === selectedFilterType && 'selected file-type')}
								onMouseUp={() => {
									setFilterLoading(true);
									dispatch(setMediaFilters({
										selectedFilterType: filter,
										selectedSortType,
									}));
									setFilterDropdownOpen(false);
								}}
							>
								{filter === selectedFilterType && (
									<Icon size={12} color={COLORS.WHITE} name={ICONS.CHECK} className="margin-right-small" />
								)}
								<span>
									{/* eslint-disable-next-line no-nested-ternary */}
									{filter === MEDIA_FILTER_TYPES.ALL
										? subtab === MEDIA_TABS.FILES
											? `${filter} files`
											: `${filter} overlays`
										: filter}
								</span>
							</div>
						))}
					</div>
				</Popover>
			</div>
	), [selectedFilterType, selectedSortType, filterDropdownOpen, mediaFilterOptions]);

	const sortDropdown = useMemo(() => (
		<div className={classNames('filter-container margin-left-small', subtab === MEDIA_TABS.RECORDINGS && 'recording')} onClick={() => setSortDropdownOpen(true)}>
			<div className={classNames('filter-label bl-font-p2', {
				sorting: selectedSortType !== MEDIA_SORT_TYPES.NONE,
				isopen: sortDropdownOpen,
			})}>
					<div className="align-items-center">
						{(
							selectedSortType === MEDIA_SORT_TYPES.RECENT_ASC
							|| selectedSortType === MEDIA_SORT_TYPES.NAME_ASC
							|| selectedSortType === MEDIA_SORT_TYPES.SHOT_NUMBER_ASC
						) && (
							<div className="sort-icon" onClick={handleClickSort}>
								<Icon size={12} color={COLORS.WHITE} name={ICONS.ARROW_UP} />
							</div>
						)}
						{(
							selectedSortType === MEDIA_SORT_TYPES.RECENT_DESC
							|| selectedSortType === MEDIA_SORT_TYPES.NAME_DESC
							|| selectedSortType === MEDIA_SORT_TYPES.SHOT_NUMBER_DESC
						) && (
							<div className="sort-icon" onClick={handleClickSort}>
								<Icon size={12} color={COLORS.WHITE} name={ICONS.ARROW_DOWN} />
							</div>
						)}
						<span>{selectedSortType === MEDIA_SORT_TYPES.NONE ? 'Sort by' : selectedSortType.split('-')[0]}</span>
					</div>
					<Icon
						name={ICONS.KEYBOARD_ARROW_DOWN}
						color={COLORS.WHITE}
						size={10}
					/>
				</div>
				<Popover open={sortDropdownOpen} close={() => setSortDropdownOpen(false)}>
					<div className='filter-options bl-font-p2'>
					<div className='filter-option-header'><span className='bl-font-c1'>SORT BY</span></div>
						<div
							className={classNames('filter-option', (selectedSortType === MEDIA_SORT_TYPES.RECENT_ASC || selectedSortType === MEDIA_SORT_TYPES.RECENT_DESC) && 'selected')}
							onMouseUp={
								() => handleSelectSort(MEDIA_SORT_TYPES.RECENT_DESC, MEDIA_SORT_TYPES.RECENT_ASC)
							}
						>
							<span>Recent</span>
						</div>
						<div
							className={classNames('filter-option', (selectedSortType === MEDIA_SORT_TYPES.NAME_ASC || selectedSortType === MEDIA_SORT_TYPES.NAME_DESC) && 'selected')}
							onMouseUp={
								() => handleSelectSort(MEDIA_SORT_TYPES.NAME_ASC, MEDIA_SORT_TYPES.NAME_DESC)
							}
						>
							<span>Name</span>
						</div>
						{subtab === MEDIA_TABS.RECORDINGS && <div
							className={classNames('filter-option', (selectedSortType === MEDIA_SORT_TYPES.SHOT_NUMBER_ASC || selectedSortType === MEDIA_SORT_TYPES.SHOT_NUMBER_DESC) && 'selected')}
							onMouseUp={
								() => handleSelectSort(
									MEDIA_SORT_TYPES.SHOT_NUMBER_DESC,
									MEDIA_SORT_TYPES.SHOT_NUMBER_ASC,
								)
							}
						>
							<span>Shot #</span>
						</div>}
					</div>
				</Popover>
			</div>
	), [selectedFilterType, selectedSortType, sortDropdownOpen, subtab]);

	return (
		<>
			<div className={classNames('search-and-upload-container position-relative', subtab === MEDIA_TABS.RECORDINGS && 'recording')}>
				<div className={classNames('search-field-media', isForModal && 'no-upload', searchString && 'filtered')}>
					<input
						className='bl-font-p2'
						type="text"
						onKeyUp={handleFilterMedia}
						placeholder={`Search ${subtab}...`}
						onChange={(e) => {
							setFilterLoading(true);
							setSearchString(e.target.value);
						}}
						value={searchString}
					/>
					<span className="search-icon">
						<Icon name={ICONS.SEARCH} color={COLORS.DEFAULT_GRAY} size={14} />
					</span>
				</div>
				{!isForModal && subtab !== MEDIA_TABS.RECORDINGS && (
					<button
						className={classNames('upload-media transparent bl-font-s3', { uploading })}
						onClick={openSelectFile}
					>
						{uploading ? null : <Icon className='margin-right-extra-small' name={ICONS.ADD} color={COLORS.PALE_GREEN} size={12} />}
						<span className='text-green white-space-nowrap'>{uploading ? 'Uploading...' : 'Upload'}</span>
					</button>
				)}
				{subtab === MEDIA_TABS.RECORDINGS && sortDropdown}
			</div>
			{subtab !== MEDIA_TABS.RECORDINGS && (
				<div className="media-filters-container">
					{filterDropdown}
					{sortDropdown}
				</div>
			)}
			{isMediaFiltered && (
				<div className={classNames('media-filters-results-container', filterLoading)}>
					<h6 className='bl-font-header margin-none' style={{ flex: 1 }}>
						{filterLoading ? loadingEl : resultsString}
					</h6>
					{!filterLoading && <button onClick={handleClearFilters} style={{backgroundColor:'rgba(0,0,0,0)'}}>
						<Icon className='margin-right-small' name={ICONS.CLOSE} color={COLORS.PALE_GREEN} size={10} />
						<span className='bl-font-s3 white-space-nowrap' style={{color: 'var(--product-acc-400)'}}>Clear filters</span>
					</button>}
				</div>
			)}
		</>
	);
}
